import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Search, Shield, AlertTriangle, Clock, CheckCircle,
  Send, Loader2, Package, Calendar, FileText, AlertCircle, Phone,
  XCircle, Truck, ClipboardList, ShieldCheck
} from 'lucide-react';
import { SUPPORT_PHONE, SUPPORT_PHONE_HREF } from '../config/contact';

const getBackendUrl = () => process.env.NODE_ENV === 'production' ? (process.env.REACT_APP_BACKEND_URL || '') : '';

// The warranty terms are served by the API (`/api/care/policy`) so this page,
// the account area and the emails all quote one document. Extended warranty
// beyond the included six months is on request only — an advisor approves
// each request before the customer can set up payment.
function WarrantyPolicy({ policy, months }) {
  if (!policy) return null;
  const card = 'p-8 rounded-2xl bg-gradient-to-b from-gray-800/50 to-transparent border border-gray-800/50';
  return (
    <motion.section
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.15 }}
      className="space-y-6 mt-8"
      aria-labelledby="warranty-policy-title"
    >
      <div className="text-center">
        <h2 id="warranty-policy-title" className="text-3xl font-bold mb-3">Warranty policy</h2>
        <p className="text-gray-400 max-w-2xl mx-auto">
          Every ClearWay includes {months} months of warranty from the day it is delivered.
          {' '}{policy.extension_note}
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <div className={card}>
          <h3 className="text-xl font-semibold mb-3 flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-green-400" />
            What the warranty covers
          </h3>
          <p className="text-gray-400 text-sm mb-5">{policy.covered_intro}</p>
          <ul className="space-y-3">
            {policy.covered.map(item => (
              <li key={item.title} className="flex items-start gap-3">
                <CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0 mt-1" />
                <div>
                  <p className="font-medium">{item.title}</p>
                  {item.sub && <p className="text-gray-500 text-sm">{item.sub}</p>}
                </div>
              </li>
            ))}
          </ul>
          <p className="text-gray-500 text-sm mt-5 pt-4 border-t border-gray-800">{policy.replacement_note}</p>
        </div>

        <div className={card}>
          <h3 className="text-xl font-semibold mb-3 flex items-center gap-2">
            <XCircle className="w-5 h-5 text-red-400" />
            What the warranty does not cover
          </h3>
          <ul className="space-y-3">
            {policy.excluded.map(text => (
              <li key={text} className="flex items-start gap-3 text-sm text-gray-400">
                <XCircle className="w-4 h-4 text-red-400/70 flex-shrink-0 mt-0.5" />
                <span>{text}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <div className={card}>
          <h3 className="text-xl font-semibold mb-4 flex items-center gap-2">
            <Truck className="w-5 h-5 text-blue-400" />
            Courier and shipping costs
          </h3>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-gray-500 border-b border-gray-800">
                <th className="pb-2 font-medium">Step</th>
                <th className="pb-2 font-medium text-right">Who pays</th>
              </tr>
            </thead>
            <tbody>
              {policy.shipping.map(row => (
                <tr key={row.step} className="border-b border-gray-800/60 last:border-0">
                  <td className="py-3 text-gray-300">{row.step}</td>
                  <td className="py-3 text-right font-semibold">{row.who}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className={card}>
          <h3 className="text-xl font-semibold mb-4 flex items-center gap-2">
            <ClipboardList className="w-5 h-5 text-blue-400" />
            How to claim
          </h3>
          <ol className="space-y-3">
            {policy.claim_steps.map((step, index) => (
              <li key={step} className="flex items-start gap-3 text-sm text-gray-300">
                <span className="w-6 h-6 rounded-full bg-blue-500/10 border border-blue-500/30 text-blue-400 text-xs font-semibold flex items-center justify-center flex-shrink-0">
                  {index + 1}
                </span>
                <span>{step}</span>
              </li>
            ))}
          </ol>
        </div>
      </div>

      <div className="p-6 rounded-2xl bg-blue-500/5 border border-blue-500/20">
        <h3 className="text-lg font-semibold mb-2 flex items-center gap-2">
          <Shield className="w-5 h-5 text-blue-400" />
          Warranty beyond six months
        </h3>
        <p className="text-gray-400 text-sm leading-relaxed">
          Cover beyond the included {months} months (ClearWay Care) extends the same cover for as long as
          you keep the plan. It is available on request only: ask from{' '}
          <a href="/account/care" className="text-blue-400 hover:underline">your account</a> or on the order
          confirmation page, an advisor reviews the request, and payment can only be set up once it has
          been approved. Nothing is charged until the included warranty ends.
        </p>
      </div>
    </motion.section>
  );
}

function WarrantyPage() {
  const [searchParams] = useSearchParams();
  const [serialNumber, setSerialNumber] = useState('');
  const [productInfo, setProductInfo] = useState(null);
  const [policy, setPolicy] = useState(null);
  const [warrantyMonths, setWarrantyMonths] = useState(6);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [issueForm, setIssueForm] = useState({
    name: '',
    email: '',
    phone: '',
    issue_type: 'malfunction',
    severity: 'medium',
    description: ''
  });

  useEffect(() => {
    let cancelled = false;
    fetch(`${getBackendUrl()}/api/care/policy`)
      .then(r => (r.ok ? r.json() : null))
      .then(data => {
        if (cancelled || !data?.policy) return;
        setPolicy(data.policy);
        if (data.warranty_months) setWarrantyMonths(data.warranty_months);
      })
      .catch(() => { /* the lookup and the contact line still work without the terms */ });
    return () => { cancelled = true; };
  }, []);

  const lookupSerial = useCallback(async (serial) => {
    if (!serial || !serial.trim()) return;
    
    setError('');
    setProductInfo(null);
    setLoading(true);
    try {
      const response = await fetch(`${getBackendUrl()}/api/serial/${serial.trim()}`);
      if (!response.ok) throw new Error('lookup_failed');
      setProductInfo(await response.json());
    } catch {
      setError('Serial number not found. Please check and try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  // Auto-lookup from URL params
  useEffect(() => {
    const serialFromUrl = searchParams.get('serial');
    if (serialFromUrl) {
      setSerialNumber(serialFromUrl.toUpperCase());
      lookupSerial(serialFromUrl);
    }
  }, [searchParams, lookupSerial]);

  const handleSubmit = (e) => {
    e.preventDefault();
    lookupSerial(serialNumber);
  };

  const submitIssue = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    setSuccess('');
    try {
      const response = await fetch(`${getBackendUrl()}/api/serial/${serialNumber.trim()}/issue`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(issueForm)
      });
      if (!response.ok) throw new Error('submit_failed');
      setIssueForm({ name: '', email: '', phone: '', issue_type: 'malfunction', severity: 'medium', description: '' });
      setSuccess('Issue submitted successfully. Our team will contact you soon.');
      // Refresh the product info
      const refreshResponse = await fetch(`${getBackendUrl()}/api/serial/${serialNumber.trim()}`);
      if (refreshResponse.ok) {
        setProductInfo(await refreshResponse.json());
      }
    } catch {
      setError('Could not submit issue. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      available: 'text-green-400 bg-green-500/10 border-green-500/30',
      sold: 'text-blue-400 bg-blue-500/10 border-blue-500/30',
      assigned: 'text-purple-400 bg-purple-500/10 border-purple-500/30',
      defective: 'text-red-400 bg-red-500/10 border-red-500/30',
    };
    return colors[status] || 'text-gray-400 bg-gray-500/10 border-gray-500/30';
  };

  const isWarrantyValid = (warrantyDate) => {
    if (!warrantyDate) return false;
    return new Date(warrantyDate) > new Date();
  };

  // The included warranty runs six months from delivery; `warranty_until` is
  // the older hand-set date, kept as a fallback for units that pre-date it.
  const warrantyDate = productInfo?.warranty_end || productInfo?.warranty_until;

  return (
    <div className="min-h-screen pt-28 pb-20 px-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-500/10 border border-blue-500/20 mb-6">
            <Shield className="w-4 h-4 text-blue-400" />
            <span className="text-blue-400 text-sm font-medium">Product Support</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold mb-4">Warranty & Support</h1>
          <p className="text-gray-400 max-w-xl mx-auto">
            Look up your ClearWay device and submit support requests.
            Enter your serial number to check warranty status and report any issues.
          </p>
          <a
            href={SUPPORT_PHONE_HREF}
            className="inline-flex items-center gap-2 mt-6 px-5 py-2.5 rounded-full bg-blue-500/10 border border-blue-500/20 text-blue-400 hover:bg-blue-500/20 transition-colors"
          >
            <Phone className="w-4 h-4" />
            <span className="font-medium">{SUPPORT_PHONE}</span>
          </a>
        </motion.div>

        {/* Serial Number Lookup */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="p-8 rounded-2xl bg-gradient-to-b from-gray-800/50 to-transparent border border-gray-800/50 mb-8"
        >
          <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
            <Search className="w-5 h-5 text-blue-400" />
            Serial Number Lookup
          </h2>
          <form onSubmit={handleSubmit}>
            <div className="flex flex-col sm:flex-row gap-3">
              <input
                type="text"
                className="flex-1 px-4 py-3 rounded-xl bg-gray-900/50 border border-gray-700 focus:border-blue-500 focus:outline-none font-mono text-lg tracking-wider"
                value={serialNumber}
                onChange={(e) => setSerialNumber(e.target.value.toUpperCase())}
                placeholder="CW2603XXXXXXXX"
                required
              />
              <button 
                type="submit"
                disabled={loading}
                className="px-6 py-3 rounded-xl bg-blue-500 hover:bg-blue-600 disabled:bg-gray-700 disabled:cursor-not-allowed transition-colors font-medium flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Looking up...
                  </>
                ) : (
                  <>
                    <Search className="w-5 h-5" />
                    Lookup
                  </>
                )}
              </button>
            </div>
            <p className="text-gray-500 text-sm mt-3">
              Find your serial number on the label attached to your ClearWay device, or scan the QR code.
            </p>
          </form>
          
          {error && !productInfo && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-4 p-4 rounded-xl bg-red-500/10 border border-red-500/30 flex items-center gap-3"
            >
              <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
              <span className="text-red-400">{error}</span>
            </motion.div>
          )}
        </motion.div>

        {/* Product Info */}
        <AnimatePresence>
          {productInfo && (
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="space-y-6"
            >
              {/* Device Details */}
              <div className="p-8 rounded-2xl bg-gradient-to-b from-gray-800/50 to-transparent border border-gray-800/50">
                <h2 className="text-xl font-semibold mb-6 flex items-center gap-2">
                  <Package className="w-5 h-5 text-blue-400" />
                  Device Information
                </h2>
                
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <div>
                      <p className="text-sm text-gray-500 mb-1">Serial Number</p>
                      <p className="font-mono text-lg bg-gray-800/50 px-3 py-2 rounded-lg inline-block">
                        {productInfo.serial_number}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500 mb-1">Product</p>
                      <p className="text-lg font-medium">{productInfo.product_name || 'ClearWay Smart Mobility Stick'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500 mb-1">Status</p>
                      <span className={`px-3 py-1 rounded-full text-sm font-medium border ${getStatusColor(productInfo.status)}`}>
                        {productInfo.status}
                      </span>
                    </div>
                  </div>
                  
                  <div className="space-y-4">
                    <div>
                      <p className="text-sm text-gray-500 mb-1">Warranty Status</p>
                      {productInfo.extended_warranty_active ? (
                        <div className="flex items-center gap-2 text-green-400">
                          <ShieldCheck className="w-5 h-5" />
                          <span className="font-medium">Extended warranty active</span>
                        </div>
                      ) : warrantyDate ? (
                        <div className={`flex items-center gap-2 ${isWarrantyValid(warrantyDate) ? 'text-green-400' : 'text-red-400'}`}>
                          {isWarrantyValid(warrantyDate) ? (
                            <>
                              <CheckCircle className="w-5 h-5" />
                              <span className="font-medium">Active</span>
                            </>
                          ) : (
                            <>
                              <AlertTriangle className="w-5 h-5" />
                              <span className="font-medium">Expired</span>
                            </>
                          )}
                        </div>
                      ) : (
                        <span className="text-gray-400">Starts on delivery</span>
                      )}
                    </div>
                    <div>
                      <p className="text-sm text-gray-500 mb-1">
                        {productInfo.extended_warranty_active ? 'Included Warranty Until' : 'Warranty Until'}
                      </p>
                      <p className="flex items-center gap-2">
                        <Calendar className="w-4 h-4 text-gray-400" />
                        {warrantyDate
                          ? new Date(warrantyDate).toLocaleDateString('en-IN', {
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric'
                            })
                          : `${warrantyMonths} months from delivery`}
                      </p>
                      {!productInfo.extended_warranty_active && warrantyDate && !isWarrantyValid(warrantyDate) && (
                        <p className="text-gray-500 text-xs mt-2">
                          Warranty beyond six months is available on request from{' '}
                          <a href="/account/care" className="text-blue-400 hover:underline">your account</a>, subject to approval.
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Prototype Notice */}
                <div className="mt-6 p-4 rounded-xl bg-yellow-500/5 border border-yellow-500/20">
                  <p className="text-yellow-400/80 text-sm flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                    <span>
                      ClearWay is a prototype demonstrator device. This product has not received regulatory approval 
                      and is provided for evaluation and testing purposes.{' '}
                      <a href="/safety-notice" className="underline hover:text-yellow-300">
                        Read our Safety Notice
                      </a>
                    </span>
                  </p>
                </div>
              </div>

              {/* Event History */}
              <div className="p-8 rounded-2xl bg-gradient-to-b from-gray-800/50 to-transparent border border-gray-800/50">
                <h2 className="text-xl font-semibold mb-6 flex items-center gap-2">
                  <Clock className="w-5 h-5 text-blue-400" />
                  Support History
                </h2>
                
                {(productInfo.recent_events || []).length === 0 ? (
                  <div className="text-center py-8">
                    <FileText className="w-12 h-12 text-gray-600 mx-auto mb-3" />
                    <p className="text-gray-400">No support history available for this device.</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {(productInfo.recent_events || []).map((event) => (
                      <div 
                        key={event.id} 
                        className="p-4 rounded-xl bg-gray-800/30 border border-gray-700/50"
                      >
                        <div className="flex items-start justify-between gap-4 mb-2">
                          <div className="flex items-center gap-2">
                            {event.event_type === 'fault_reported' ? (
                              <AlertTriangle className="w-4 h-4 text-red-400" />
                            ) : (
                              <CheckCircle className="w-4 h-4 text-green-400" />
                            )}
                            <span className="font-medium capitalize">
                              {event.event_type.replace(/_/g, ' ')}
                            </span>
                          </div>
                          <span className={`px-2 py-0.5 rounded text-xs ${
                            event.status === 'open' 
                              ? 'bg-yellow-500/10 text-yellow-400 border border-yellow-500/30' 
                              : 'bg-green-500/10 text-green-400 border border-green-500/30'
                          }`}>
                            {event.status}
                          </span>
                        </div>
                        {event.description && (
                          <p className="text-gray-400 text-sm mb-2">{event.description}</p>
                        )}
                        {event.resolution && (
                          <p className="text-green-400/80 text-sm">
                            <span className="font-medium">Resolution:</span> {event.resolution}
                          </p>
                        )}
                        <p className="text-gray-500 text-xs mt-2">
                          {event.created_at && new Date(event.created_at).toLocaleString('en-IN')}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Report Issue Form */}
              <div className="p-8 rounded-2xl bg-gradient-to-b from-gray-800/50 to-transparent border border-gray-800/50">
                <h2 className="text-xl font-semibold mb-6 flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-orange-400" />
                  Report an Issue
                </h2>
                
                {success && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mb-6 p-4 rounded-xl bg-green-500/10 border border-green-500/30 flex items-center gap-3"
                  >
                    <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />
                    <span className="text-green-400">{success}</span>
                  </motion.div>
                )}

                {error && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mb-6 p-4 rounded-xl bg-red-500/10 border border-red-500/30 flex items-center gap-3"
                  >
                    <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
                    <span className="text-red-400">{error}</span>
                  </motion.div>
                )}
                
                <form onSubmit={submitIssue} className="space-y-4">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm text-gray-400 mb-2">Your Name *</label>
                      <input 
                        className="w-full px-4 py-3 rounded-xl bg-gray-900/50 border border-gray-700 focus:border-blue-500 focus:outline-none" 
                        placeholder="Full name"
                        value={issueForm.name} 
                        onChange={(e) => setIssueForm({ ...issueForm, name: e.target.value })} 
                        required 
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-gray-400 mb-2">Email Address *</label>
                      <input 
                        className="w-full px-4 py-3 rounded-xl bg-gray-900/50 border border-gray-700 focus:border-blue-500 focus:outline-none" 
                        type="email" 
                        placeholder="you@example.com"
                        value={issueForm.email} 
                        onChange={(e) => setIssueForm({ ...issueForm, email: e.target.value })} 
                        required 
                      />
                    </div>
                  </div>
                  
                  <div className="grid md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm text-gray-400 mb-2">Phone (Optional)</label>
                      <input 
                        className="w-full px-4 py-3 rounded-xl bg-gray-900/50 border border-gray-700 focus:border-blue-500 focus:outline-none" 
                        placeholder="+91 XXXXX XXXXX"
                        value={issueForm.phone} 
                        onChange={(e) => setIssueForm({ ...issueForm, phone: e.target.value })} 
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-gray-400 mb-2">Issue Type</label>
                      <select 
                        className="w-full px-4 py-3 rounded-xl bg-gray-900/50 border border-gray-700 focus:border-blue-500 focus:outline-none appearance-none cursor-pointer" 
                        value={issueForm.issue_type} 
                        onChange={(e) => setIssueForm({ ...issueForm, issue_type: e.target.value })}
                      >
                        <option value="malfunction">Malfunction</option>
                        <option value="battery">Battery Issue</option>
                        <option value="sensor">Sensor Issue</option>
                        <option value="damage">Physical Damage</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm text-gray-400 mb-2">Severity</label>
                      <select 
                        className="w-full px-4 py-3 rounded-xl bg-gray-900/50 border border-gray-700 focus:border-blue-500 focus:outline-none appearance-none cursor-pointer" 
                        value={issueForm.severity} 
                        onChange={(e) => setIssueForm({ ...issueForm, severity: e.target.value })}
                      >
                        <option value="low">Low - Minor inconvenience</option>
                        <option value="medium">Medium - Affects usage</option>
                        <option value="high">High - Cannot use device</option>
                      </select>
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm text-gray-400 mb-2">Describe the Issue *</label>
                    <textarea 
                      className="w-full px-4 py-3 rounded-xl bg-gray-900/50 border border-gray-700 focus:border-blue-500 focus:outline-none resize-none" 
                      rows="4" 
                      placeholder="Please describe the issue you're experiencing in detail..."
                      value={issueForm.description} 
                      onChange={(e) => setIssueForm({ ...issueForm, description: e.target.value })} 
                      required 
                    />
                  </div>
                  
                  <button 
                    type="submit"
                    disabled={submitting}
                    className="px-6 py-3 rounded-xl bg-orange-500 hover:bg-orange-600 disabled:bg-gray-700 disabled:cursor-not-allowed transition-colors font-medium flex items-center gap-2"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        Submitting...
                      </>
                    ) : (
                      <>
                        <Send className="w-5 h-5" />
                        Submit Issue Report
                      </>
                    )}
                  </button>
                </form>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Help Text */}
        {!productInfo && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="text-center text-gray-500 mt-12"
          >
            <p className="mb-2">Need help finding your serial number?</p>
            <p className="text-sm">
              The serial number is printed on a label attached to your ClearWay device.
              You can also scan the QR code on the label to access this page directly.
            </p>
          </motion.div>
        )}

        <WarrantyPolicy policy={policy} months={warrantyMonths} />
      </div>
    </div>
  );
}

export default WarrantyPage;
